import { generatePath, useNavigate } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconBank } from '@components/icons/bank';
import { IconCreditCard } from '@components/icons/credit-card';
import { IconDocument } from '@components/icons/document';
import { IconDownload } from '@components/icons/download';
import { IconPlan } from '@components/icons/icon-plan';
import { IconList } from '@components/icons/list';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { LabelTextGroup } from '@components/label-text-group';
import { ListCard } from '@components/list-card';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { codeMap } from '@components/phone-field/country-data';
import { ProgressBar } from '@components/progress-bar';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';

import { useCurrentUser } from 'api/authentication/hooks';
import { CurrentUser } from 'api/authentication/requests';
import {
  useBillingDetails,
  useGetChargeBeePaymentMethodPage,
  useGetInvoicePdf,
  useListInvoices,
} from 'api/company/hooks';
import { BillingAddress, BillingDetails, Invoice } from 'api/company/requests';
import { routePaths } from 'ui/authenticated/navigation';

import { useEditBillingInfo } from './billing-address-modal';

export const BillingPage = () => {
  const { t } = useScopedTranslate(locale);
  const user = useCurrentUser();
  const { data, isLoading } = useBillingDetails();

  if (isLoading) {
    return <Spinner />;
  }

  const tabs = [
    {
      label: t('overview'),
      section: 'overview',
      icon: <IconList />,
      content: (
        <PageContent>
          <OverviewTab billingData={data} user={user} />
        </PageContent>
      ),
    },
    {
      label: t('invoices'),
      section: 'invoices',
      icon: <IconDocument />,
      content: (
        <PageContent>
          <InvoicesTab billingAddress={data?.billingAddress} user={user} canEdit={Boolean(data)} />
        </PageContent>
      ),
    },
  ];

  return (
    <RouterTabsHeader breadcrumbs={<Breadcrumb items={[{ label: t('billing') }]} />} tabs={tabs} />
  );
};

const OverviewTab = ({
  billingData,
  user,
}: {
  billingData?: BillingDetails;
  user?: CurrentUser;
}) => {
  const { t } = useScopedTranslate(locale);
  const chargebeePaymentMethodPageQuery = useGetChargeBeePaymentMethodPage();
  const navigate = useNavigate();
  const paymentMethodPageUrl = chargebeePaymentMethodPageQuery.data?.url;
  const card = billingData?.card;
  const cardIsExpired = card?.status === 'expired';
  const isBankTransfer = billingData?.paymentMethod === 'bank_transfer';
  const plan = user?.plan;
  const isOnFreePlan = plan?.planType === 'trial';

  const getPaymentDescription = () => {
    if (card) {
      if (cardIsExpired) {
        return styledTranslate({
          key: 'cardExpiredStyled',
          variables: {
            expired: {
              text: t('cardExpired'),
              color: 'red100',
            },
          },
          translations: locale,
        });
      } else {
        return t('cardInfo', {
          card_type: card.cardType,
          last4: card.last4,
          exp_month: card.expMonth,
          exp_year: card.expYear,
        });
      }
    } else if (isBankTransfer) {
      return t('bankTransfer');
    } else {
      return t('noMethod');
    }
  };

  // Capped at the allotment: the counter always reads "used / allotment", so a
  // plan running past it on a hardcap shows 50,000 / 50,000, never 60,000 / 50,000.
  const creditsUsed = Math.min(plan?.creditsUsed ?? 0, plan?.creditsPerPeriod ?? 0);

  return (
    <PageContainers.MiddleColumnPage>
      <Stack space={6}>
        {plan && (
          <Card variant="gradient" space={6} className="dark">
            <Stack space={4}>
              <Stack orientation="horizontal" justify="space-between">
                <div>
                  <Text font="bold18" color="white100" text={t(`plan${plan.planType}`)} />
                  {isOnFreePlan && plan.dateToClose && (
                    <Text
                      font="regular14"
                      color="textSecondary"
                      text={t('trialEnds', {
                        endDate: abbreviateDate(plan.dateToClose, 'en', 'date-fancy-short'),
                      })}
                    />
                  )}
                  {!isOnFreePlan && plan.dateToReset && (
                    <Text
                      font="regular14"
                      color="textSecondary"
                      text={t('usageReset', {
                        date: abbreviateDate(plan.dateToReset, 'en', 'date-fancy-short'),
                      })}
                    />
                  )}
                </div>
                <Button
                  variant="secondary"
                  iconLeft={<IconPlan />}
                  text={t('managePlan')}
                  onClick={() => navigate(generatePath(routePaths.subscriptionPlans))}
                />
              </Stack>
              {isOnFreePlan && (
                <Text font="regular14" color="textSecondary" text={t('creditsIncluded')} />
              )}
              <Stack space={1}>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold12" color="textSecondary" text={t('actionCredits')} />
                  <Text
                    font="semibold12"
                    color="textSecondary"
                    text={`${formatNumber(creditsUsed)} / ${formatNumber(plan.creditsPerPeriod)}`}
                  />
                </Stack>
                <ProgressBar ratio={creditsUsed / plan.creditsPerPeriod} variant={'info'} />
                {plan.creditStatus === 'paused' && (
                  <Text font="semibold12" color="textNegative" text={t('limitReached')} />
                )}
                {plan.creditStatus === 'over_base_limit' && (
                  <Text
                    font="semibold12"
                    color="textSecondary"
                    text={t('overBaseLimit', {
                      included: formatNumber(plan.creditsPerPeriod),
                      extra: formatNumber(plan.creditsUsed - plan.creditsPerPeriod),
                    })}
                  />
                )}
              </Stack>
            </Stack>
          </Card>
        )}
        <Separator />
        <Stack space={4}>
          <Stack space={2}>
            <Text font="heading2" color="textPrimary" text={t('billing')} />
            <Text
              font="regular14"
              color="textSecondary"
              text={styledTranslate({
                key: 'forQuestions',
                variables: {
                  email: {
                    color: 'textLink',
                    font: 'regular14',
                    text: <a href={'mailto:billing@50skills.com'}>{t('billingEmail')}</a>,
                  },
                },
                translations: locale,
              })}
            />
          </Stack>
          <ListCard
            title={t('paymentMethod')}
            description={getPaymentDescription()}
            icon={
              isBankTransfer ? (
                <IconBank color="elementPurple80" />
              ) : (
                <IconCreditCard color="elementPurple80" />
              )
            }
            actions={
              !isBankTransfer && !isOnFreePlan ? (
                <Button
                  iconRight={<IconOpenNewTab size={12} />}
                  variant="ghost"
                  isDisabled={chargebeePaymentMethodPageQuery.isLoading}
                  text={card ? t('core.update') : t('core.add')}
                  onClick={() => window.open(paymentMethodPageUrl, '_blank')}
                />
              ) : null
            }
          />
        </Stack>
      </Stack>
    </PageContainers.MiddleColumnPage>
  );
};

const InvoicesTab = ({
  billingAddress,
  canEdit,
  user,
}: {
  billingAddress?: BillingAddress;
  canEdit: boolean;
  user?: CurrentUser;
}) => {
  const { t } = useScopedTranslate(locale);
  const showEditModal = useEditBillingInfo();
  const { data: invoices, isLoading } = useListInvoices();

  const company = user?.company;

  return (
    <PageContainers.MiddleColumnPage>
      <Stack space={6}>
        <Card space={6} variant="transparent">
          <Stack space={4}>
            <Stack orientation="horizontal" space={4} align="center" justify="space-between">
              <div>
                <Text font="semibold14" text={t('billingDetails')} />
                {!billingAddress && (
                  <Text font="regular14" text={t('noDetails')} color="textHelper" />
                )}
              </div>
              {canEdit && (
                <Button
                  variant="secondary"
                  text={t('core.edit')}
                  onClick={() => showEditModal({ billingAddress })}
                />
              )}
            </Stack>
            {billingAddress && (
              <Stack space={2}>
                <LabelTextGroup label={t('companyName')} text={billingAddress.company} />
                <div>
                  <LabelTextGroup label={t('address')} text={billingAddress.line1} />
                  {billingAddress.zip && billingAddress.city && (
                    <Text
                      font="regular14"
                      text={`${billingAddress.zip} ${billingAddress.city} ${
                        billingAddress.state ?? ''
                      }`}
                    />
                  )}
                  {company?.isPaydayCustomer ? (
                    <Text font="regular14" text={billingAddress.country} />
                  ) : (
                    <Text
                      font="regular14"
                      text={codeMap[billingAddress.country as keyof typeof codeMap]}
                    />
                  )}
                </div>
                <LabelTextGroup label={t('phone')} text={billingAddress.phone} />
                <LabelTextGroup label={t('email')} text={billingAddress.email} />
                {billingAddress.ssn && (
                  <LabelTextGroup label={t('ssn')} text={billingAddress.ssn} />
                )}
              </Stack>
            )}
          </Stack>
        </Card>
        <Stack space={2}>
          <Text font="semibold14" text={t('invoices')} />
          {invoices && invoices.length > 0 ? (
            <>
              {invoices.map((invoice) => (
                <InvoiceCard key={invoice.id} invoice={invoice} />
              ))}
            </>
          ) : isLoading ? (
            <RelativeSpinner />
          ) : (
            <Card space={6}>
              <Stack orientation="horizontal" space={4}>
                <IconDocument color="elementPurple60" />
                <Text font="semibold14" color="textSecondary" text={t('noInvoices')} />
              </Stack>
            </Card>
          )}
        </Stack>
      </Stack>
    </PageContainers.MiddleColumnPage>
  );
};

const InvoiceCard = ({ invoice }: { invoice: Invoice }) => {
  const { t } = useScopedTranslate(locale);
  const getInvoiceUrlQuery = useGetInvoicePdf(invoice.id);

  const invoiceData = getInvoiceUrlQuery.data;

  const handleViewInvoice = async () => {
    if (invoiceData) {
      return window.open(invoiceData.url, '_blank');
    }
  };

  return (
    <Card space={4}>
      <Stack orientation="horizontal" space={4} align="center" justify="space-between">
        <Stack orientation="horizontal" space={4} align="center">
          <IconDocument color="elementPurpleInvert" />
          <div>
            <Text font="semibold14" text={abbreviateDate(invoice.date, 'en', 'date-fancy-short')} />
            <Text
              font="regular14"
              color="textSecondary"
              text={t('invoiceStatus', {
                status: t(invoice.status),
                amount: invoice.amount,
              })}
            />
          </div>
        </Stack>
        <Button
          iconLeft={<IconDownload color="buttonIconPositive" />}
          variant="ghost"
          onClick={handleViewInvoice}
          isDisabled={!invoiceData}
          tooltip={invoiceData ? undefined : t('errorLoadingInvoice')}
        />
      </Stack>
    </Card>
  );
};

export const locale = {
  en: {
    billing: 'Billing',
    overview: 'Overview',
    invoices: 'Invoices',
    forQuestions: 'For questions about billing, contact {email}',
    billingEmail: 'billing@50skills.com',
    paymentMethod: 'Payment method',
    noMethod: 'No payment method added',
    currentPlan: 'Current plan',
    onFreePlan: 'Your company is on the free plan',
    billingDetails: 'Billing Address',
    noDetails: 'No details added',
    receiveEamil: 'Receive email with new invoices',
    receiveEmailDescription: 'Invoices are sent to the email address listed below',
    noInvoices: 'You do not have any invoices',
    cardInfo: '{card_type} **** {last4} (Expires {exp_month}/{exp_year})',
    companyName: 'Company name',
    address: 'Address',
    email: 'Email',
    phone: 'Phone',
    state: 'State',
    invoiceStatus: '{status} • {amount}',
    errorLoadingInvoice:
      'Unable to load the invoice PDF. Try again in a moment or contact us for assistance',
    cardExpiredStyled: '{expired}',
    cardExpired: 'Card expired',
    bankTransfer: 'Direct Bank Transfer',
    noBillingDetails: 'No billing details',
    nodetailsHelpText: 'Unable to load billing details.',
    ssn: 'Social Security Number',
    actionCredits: 'Action credits',
    trialEnds: 'Free trial ends {endDate}',
    managePlan: 'Manage plan',
    creditsIncluded: '500 Credits included in free trial',
    plantrial: 'Free Premium trial',
    planbusiness: 'Business',
    planpremium: 'Premium',
    planenterprise: 'Enterprise',
    plandemo_internal: 'Demo internal',
    plandemo_external: 'Demo external',
    usageReset: 'Usage reset: {date}',
    limitReached: 'Credit limit reached',
    overBaseLimit:
      'You’ve used all {included} included credits and {extra} additional billable credits.',
    paid: 'Paid',
    posted: 'Posted',
    payment_due: 'Payment due',
    not_paid: 'Not paid',
    voided: 'Voided',
    pending: 'Pending',
    sent: 'Sent',
  },
};
